import React, { useState } from "react";
import { Switch } from "@headlessui/react";

const InsuranceOptionsPage = ({ onNext, onBack, formData }) => {
  const [selectedInsurance, setSelectedInsurance] = useState({
    liability: true,
    waterSkiing: false,
    waterskiCapacity: "1", // slider 1..2
    casco: false,
    cascoValue: "",
    trailerInsurance: false,
    trailerValue: "",
    dinghyInsurance: false,
    dinghyValue: "",
    trackAndTrace: false,
    rescueCosts: false,
    rescueAmount: "",
    personalAccidents: false,
    accidentPassengers: "",
    legalAssistance: false,
  });

  const [errors, setErrors] = useState({});
  const boatAge = new Date().getFullYear() - formData.boatYear;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSelectedInsurance((prevState) => ({
      ...prevState,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const validate = () => {
    let validationErrors = {};
    if (selectedInsurance.casco && !selectedInsurance.cascoValue) {
      validationErrors.cascoValue = "Voer de waarde van het vaartuig in.";
    }
    if (
      selectedInsurance.casco &&
      selectedInsurance.trailerInsurance &&
      !selectedInsurance.trailerValue
    ) {
      validationErrors.trailerValue = "Voer de waarde van de trailer in.";
    }
    if (
      selectedInsurance.casco &&
      selectedInsurance.dinghyInsurance &&
      !selectedInsurance.dinghyValue
    ) {
      validationErrors.dinghyValue = "Voer de waarde van de bijboot in.";
    }
    if (selectedInsurance.rescueCosts && !selectedInsurance.rescueAmount) {
      validationErrors.rescueAmount =
        "Selecteer een bedrag voor lichtingskosten.";
    }
    if (
      selectedInsurance.personalAccidents &&
      !selectedInsurance.accidentPassengers
    ) {
      validationErrors.accidentPassengers =
        "Selecteer het aantal passagiers voor de ongevallenverzekering.";
    }
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const handleNext = () => {
    if (validate()) {
      const updatedFormData = {
        ...formData,
        selectedInsurance: selectedInsurance,
      };
      onNext(updatedFormData);
    }
  };

  // Helper voor Switch toggles
  const toggle =
    (key, after = () => {}) =>
    (checked) => {
      setSelectedInsurance((prev) => {
        const next = { ...prev, [key]: checked };
        after(next, checked);
        return next;
      });
    };

  return (
    <div className="p-8">
      <div className="mx-auto max-w-3xl p-6">
        <h1 className="text-3xl font-semibold mb-6 text-left text-[#1a2a6c]">
          Welke waarborgen wens je graag te onderschrijven?
        </h1>
        <p className="text-lg font-medium text-[#1a2a6c] mb-6">
          Laat ons weten welke waarborgen je graag wenst
        </p>

        <form className="space-y-6">
          {/* Liability (Verplicht) */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
            <label className="text-lg font-semibold text-[#1a2a6c]">
              Burgerlijke aansprakelijkheid (verplicht)
            </label>
            <Switch
              checked={selectedInsurance.liability}
              onChange={() => {}}
              disabled
              className="bg-[#1a2a6c] ml-2 relative inline-flex h-6 w-11 items-center rounded-full transition-colors opacity-50"
            >
              <span className="translate-x-6 bg-[#ff6f61] inline-block h-4 w-4 transform rounded-full transition" />
            </Switch>
          </div>

          {/* Casco */}
          {boatAge <= 20 && (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                <label className="text-lg font-semibold text-[#1a2a6c]">
                  Wens je je boot ook in casco te verzekeren?
                </label>
                <Switch
                  checked={selectedInsurance.casco}
                  onChange={(checked) =>
                    setSelectedInsurance((prev) => {
                      if (!checked) {
                        // reset subvelden als casco uitgaat
                        return {
                          ...prev,
                          casco: false,
                          cascoValue: "",
                          trailerInsurance: false,
                          trailerValue: "",
                          dinghyInsurance: false,
                          dinghyValue: "",
                          trackAndTrace: false,
                        };
                      }
                      return { ...prev, casco: true };
                    })
                  }
                  className={`${
                    selectedInsurance.casco ? "bg-[#1a2a6c]" : "bg-gray-200"
                  } ml-2 relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                >
                  <span
                    className={`${
                      selectedInsurance.casco
                        ? "translate-x-6 bg-[#ff6f61]"
                        : "translate-x-1 bg-white"
                    } inline-block h-4 w-4 transform rounded-full transition`}
                  />
                </Switch>
              </div>

              {/* SUBSECTIE van Casco met insprong */}
              {selectedInsurance.casco && (
                <div className="mt-2 pl-6 border-l-2 border-[#ff6f61] space-y-4">
                  {/* Waarde casco */}
                  <div>
                    <label
                      htmlFor="cascoValue"
                      className="font-medium text-sm text-[#1a2a6c]"
                    >
                      Actuele waarde van het vaartuig
                    </label>
                    <input
                      type="number"
                      id="cascoValue"
                      name="cascoValue"
                      value={selectedInsurance.cascoValue}
                      onChange={handleChange}
                      className={`mt-1 p-2 block w-full border ${
                        errors.cascoValue
                          ? "border-red-500"
                          : "border-[#ff6f61]"
                      } rounded-md focus:ring-2 focus:ring-[#ff6f61]`}
                    />
                    {errors.cascoValue && (
                      <p className="text-red-500 text-sm">
                        {errors.cascoValue}
                      </p>
                    )}
                  </div>

                  {/* Trailer verzekeren? */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <label className="text-lg font-semibold text-[#1a2a6c]">
                      Wens je ook je trailer te verzekeren?
                    </label>
                    <Switch
                      checked={selectedInsurance.trailerInsurance}
                      onChange={toggle("trailerInsurance")}
                      className={`${
                        selectedInsurance.trailerInsurance
                          ? "bg-[#1a2a6c]"
                          : "bg-gray-200"
                      } ml-2 relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                    >
                      <span
                        className={`${
                          selectedInsurance.trailerInsurance
                            ? "translate-x-6 bg-[#ff6f61]"
                            : "translate-x-1 bg-white"
                        } inline-block h-4 w-4 transform rounded-full transition`}
                      />
                    </Switch>
                  </div>

                  {selectedInsurance.trailerInsurance && (
                    <div>
                      <label
                        htmlFor="trailerValue"
                        className="text-sm font-medium text-[#1a2a6c]"
                      >
                        Waarde van de trailer
                      </label>
                      <input
                        type="number"
                        id="trailerValue"
                        name="trailerValue"
                        value={selectedInsurance.trailerValue}
                        onChange={handleChange}
                        className={`mt-1 p-2 block w-full border ${
                          errors.trailerValue
                            ? "border-red-500"
                            : "border-[#ff6f61]"
                        } rounded-md focus:ring-2 focus:ring-[#ff6f61]`}
                      />
                      {errors.trailerValue && (
                        <p className="text-red-500 text-sm">
                          {errors.trailerValue}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Bijboot verzekeren? */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <label className="text-lg font-semibold text-[#1a2a6c]">
                      Wens je ook je bijboot te verzekeren?
                    </label>
                    <Switch
                      checked={selectedInsurance.dinghyInsurance}
                      onChange={toggle("dinghyInsurance")}
                      className={`${
                        selectedInsurance.dinghyInsurance
                          ? "bg-[#1a2a6c]"
                          : "bg-gray-200"
                      } ml-2 relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                    >
                      <span
                        className={`${
                          selectedInsurance.dinghyInsurance
                            ? "translate-x-6 bg-[#ff6f61]"
                            : "translate-x-1 bg-white"
                        } inline-block h-4 w-4 transform rounded-full transition`}
                      />
                    </Switch>
                  </div>

                  {selectedInsurance.dinghyInsurance && (
                    <div>
                      <label
                        htmlFor="dinghyValue"
                        className="text-sm font-medium text-[#1a2a6c]"
                      >
                        Waarde van de bijboot
                      </label>
                      <input
                        type="number"
                        id="dinghyValue"
                        name="dinghyValue"
                        value={selectedInsurance.dinghyValue}
                        onChange={handleChange}
                        className={`mt-1 p-2 block w-full border ${
                          errors.dinghyValue
                            ? "border-red-500"
                            : "border-[#ff6f61]"
                        } rounded-md focus:ring-2 focus:ring-[#ff6f61]`}
                      />
                      {errors.dinghyValue && (
                        <p className="text-red-500 text-sm">
                          {errors.dinghyValue}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Track & Trace */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                    <label className="text-lg font-semibold text-[#1a2a6c]">
                      Is er een Track & Trace aanwezig?
                    </label>
                    <Switch
                      checked={selectedInsurance.trackAndTrace}
                      onChange={toggle("trackAndTrace")}
                      className={`${
                        selectedInsurance.trackAndTrace
                          ? "bg-[#1a2a6c]"
                          : "bg-gray-200"
                      } ml-2 relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                    >
                      <span
                        className={`${
                          selectedInsurance.trackAndTrace
                            ? "translate-x-6 bg-[#ff6f61]"
                            : "translate-x-1 bg-white"
                        } inline-block h-4 w-4 transform rounded-full transition`}
                      />
                    </Switch>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Lichtingskosten / hulp / berging */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
            <label className="text-lg font-semibold text-[#1a2a6c]">
              Wens je lichtingskosten, hulp en berging te verzekeren?
            </label>
            <Switch
              checked={selectedInsurance.rescueCosts}
              onChange={toggle("rescueCosts")}
              className={`${
                selectedInsurance.rescueCosts ? "bg-[#1a2a6c]" : "bg-gray-200"
              } ml-2 relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
            >
              <span
                className={`${
                  selectedInsurance.rescueCosts
                    ? "translate-x-6 bg-[#ff6f61]"
                    : "translate-x-1 bg-white"
                } inline-block h-4 w-4 transform rounded-full transition`}
              />
            </Switch>
          </div>

          {selectedInsurance.rescueCosts && (
            <div className="mt-2">
              <label htmlFor="rescueAmount" className="font-medium">
                Selecteer een bedrag
              </label>
              <select
                id="rescueAmount"
                name="rescueAmount"
                value={selectedInsurance.rescueAmount}
                onChange={handleChange}
                className={`mt-1 p-2 block w-full border ${
                  errors.rescueAmount ? "border-red-500" : "border-[#ff6f61]"
                } rounded-md focus:ring-2 focus:ring-[#ff6f61]`}
              >
                <option value="">Selecteer een bedrag</option>
                <option value="25000">25.000 EUR</option>
                <option value="45000">45.000 EUR</option>
                <option value="75000">75.000 EUR</option>
                <option value="custom">Op aanvraag</option>
              </select>
              {errors.rescueAmount && (
                <p className="text-red-500 text-sm">{errors.rescueAmount}</p>
              )}
            </div>
          )}

          {/* Waterskiërs (nu onder lichtingskosten) */}
          {(formData.boatType === "RIB" ||
            formData.boatType === "Jetski" ||
            formData.boatType === "Motorboot") && (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
                <label className="text-lg font-semibold text-[#1a2a6c]">
                  Waterskiërs ook verzekeren?
                </label>
                <Switch
                  checked={selectedInsurance.waterSkiing}
                  onChange={toggle("waterSkiing")}
                  className={`${
                    selectedInsurance.waterSkiing
                      ? "bg-[#1a2a6c]"
                      : "bg-gray-200"
                  } ml-2 relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                >
                  <span
                    className={`${
                      selectedInsurance.waterSkiing
                        ? "translate-x-6 bg-[#ff6f61]"
                        : "translate-x-1 bg-white"
                    } inline-block h-4 w-4 transform rounded-full transition`}
                  />
                </Switch>
              </div>

              {selectedInsurance.waterSkiing && (
                <div className="mt-2">
                  <label
                    htmlFor="waterskiCapacity"
                    className="text-sm font-medium text-[#1a2a6c]"
                  >
                    Hoeveel personen kun je voorttrekken?
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="2"
                    step="1"
                    id="waterskiCapacity"
                    name="waterskiCapacity"
                    value={selectedInsurance.waterskiCapacity}
                    onChange={handleChange}
                    className="mt-2 w-full accent-[#ff6f61]"
                  />
                  <div className="text-sm mt-1 text-[#1a2a6c] font-medium">
                    {selectedInsurance.waterskiCapacity}{" "}
                    {selectedInsurance.waterskiCapacity === "1"
                      ? "persoon"
                      : "personen"}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Persoonlijke ongevallen */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
            <label className="text-lg font-semibold text-[#1a2a6c]">
              Wens je je passagiers ook voor persoonlijke ongevallen te
              verzekeren?
            </label>
            <Switch
              checked={selectedInsurance.personalAccidents}
              onChange={toggle("personalAccidents")}
              className={`${
                selectedInsurance.personalAccidents
                  ? "bg-[#1a2a6c]"
                  : "bg-gray-200"
              } ml-2 relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
            >
              <span
                className={`${
                  selectedInsurance.personalAccidents
                    ? "translate-x-6 bg-[#ff6f61]"
                    : "translate-x-1 bg-white"
                } inline-block h-4 w-4 transform rounded-full transition`}
              />
            </Switch>
          </div>

          {selectedInsurance.personalAccidents && (
            <div className="mt-2">
              <label htmlFor="accidentPassengers" className="font-medium">
                Selecteer aantal passagiers
              </label>
              <select
                id="accidentPassengers"
                name="accidentPassengers"
                value={selectedInsurance.accidentPassengers}
                onChange={handleChange}
                className={`mt-1 p-2 block w-full border ${
                  errors.accidentPassengers
                    ? "border-red-500"
                    : "border-[#ff6f61]"
                } rounded-md focus:ring-2 focus:ring-[#ff6f61]`}
              >
                <option value="">Selecteer aantal passagiers</option>
                <option value="5">5 passagiers</option>
                <option value="10">10 passagiers</option>
                <option value="15">15 passagiers</option>
              </select>
              {errors.accidentPassengers && (
                <p className="text-red-500 text-sm">
                  {errors.accidentPassengers}
                </p>
              )}
            </div>
          )}

          {/* Rechtsbijstand */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
            <label className="text-lg font-semibold text-[#1a2a6c]">
              Wens je ook rechtsbijstand?
            </label>
            <Switch
              checked={selectedInsurance.legalAssistance}
              onChange={toggle("legalAssistance")}
              className={`${
                selectedInsurance.legalAssistance
                  ? "bg-[#1a2a6c]"
                  : "bg-gray-200"
              } ml-2 relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
            >
              <span
                className={`${
                  selectedInsurance.legalAssistance
                    ? "translate-x-6 bg-[#ff6f61]"
                    : "translate-x-1 bg-white"
                } inline-block h-4 w-4 transform rounded-full transition`}
              />
            </Switch>
          </div>

          <div className="flex justify-between mt-6 gap-4">
            <button
              type="button"
              onClick={onBack}
              className="w-auto py-2 px-4 bg-[#1a2a6c] text-[#ff6f61] rounded-md text-sm font-medium hover:bg-[#142057]"
            >
              Terug
            </button>
            <button
              type="button"
              onClick={handleNext}
              className="w-auto py-2 px-4 bg-[#1a2a6c] text-[#ff6f61] rounded-md text-sm font-medium hover:bg-[#142057]"
            >
              Volgende
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InsuranceOptionsPage;
